import { useState, useEffect, useRef } from 'react';
import JSZip from 'jszip';
import { Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../providers/ThemeContext';
import { HOSTING_PLANS, formatZAR, getDaysRemaining, isSubscriptionActive, formatBytes } from '@wranglr/shared';
import type { Site } from '@wranglr/shared';
import * as api from '../services/api';
import './DashboardPage.css';
import SecurityPage from './SecurityPage';

import Editor from '@monaco-editor/react';
import { FileTree } from '../components/FileTree';
import { ThemeSwitcher } from '../components/common/ThemeSwitcher';

// Helper to detect language from file extension
function getLanguageFromFilename(filename: string): string {
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    const langMap: Record<string, string> = {
        'html': 'html', 'htm': 'html',
        'css': 'css', 'scss': 'scss', 'sass': 'scss', 'less': 'less',
        'js': 'javascript', 'jsx': 'javascript', 'mjs': 'javascript',
        'ts': 'typescript', 'tsx': 'typescript',
        'json': 'json',
        'xml': 'xml', 'svg': 'xml',
        'md': 'markdown',
        'py': 'python',
        'sh': 'shell', 'bash': 'shell',
        'yml': 'yaml', 'yaml': 'yaml',
        'sql': 'sql',
        'php': 'php',
    };
    return langMap[ext] || 'plaintext';
}

// Helper to check if file is an image
function isImageFile(filename: string): boolean {
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'ico', 'bmp'].includes(ext);
}

// Overview Tab (New "Powerhouse" Landing)
function OverviewTab() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [sites, setSites] = useState<Site[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadSites();
    }, []);

    const loadSites = async () => {
        const res = await api.getSites();
        if (res.success && res.data) {
            setSites(res.data);
        }
        setLoading(false);
    };

    const totalStorage = sites.reduce((acc, site) => acc + (site.storage_used || 0), 0);
    const formatStorage = (bytes: number) => {
        if (bytes === 0) return '0 MB';
        const mb = bytes / (1024 * 1024);
        if (mb < 1000) return `${mb.toFixed(1)} MB`;
        return `${(mb / 1024).toFixed(2)} GB`;
    };

    return (
        <div className="tab-content overview-tab">
            <div className="welcome-banner">
                <h1>Welcome back, {user?.email?.split('@')[0]}! 👋</h1>
                <p className="subtitle">Your high-performance static hosting control center.</p>
            </div>

            <div className="overview-stats-row">
                <div className="stat-widget">
                    <span className="widget-icon">🟢</span>
                    <div className="widget-content">
                        <span className="widget-label">System Status</span>
                        <span className="widget-value healthy">Operational</span>
                    </div>
                </div>
                <div className="stat-widget">
                    <span className="widget-icon">🛡️</span>
                    <div className="widget-content">
                        <span className="widget-label">Security Tier</span>
                        <span className="widget-value">Enterprise Edge</span>
                    </div>
                </div>
                <div className="stat-widget">
                    <span className="widget-icon">💾</span>
                    <div className="widget-content">
                        <span className="widget-label">Storage Used</span>
                        <span className="widget-value">{formatStorage(totalStorage)}</span>
                    </div>
                </div>
            </div>

            <div className="overview-grid">
                {/* Getting Started Card */}
                <div className="card overview-card">
                    <h3>🚀 Quick Actions</h3>
                    <div className="steps-list" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div className="step-item" style={{ alignItems: 'center', borderBottom: '1px solid var(--border-color)', paddingBottom: '1rem', marginBottom: 0 }}>
                            <div className="step-text">
                                <strong>Add a Site</strong>
                                <p style={{ margin: 0 }}>Deploy a new static site to the Edge.</p>
                            </div>
                            <button className="btn btn-sm btn-primary" onClick={() => navigate('/dashboard/sites')} style={{ marginLeft: 'auto' }}>
                                + Add Site
                            </button>
                        </div>

                        <div className="step-item" style={{ alignItems: 'center', borderBottom: '1px solid var(--border-color)', paddingBottom: '1rem', marginBottom: 0 }}>
                            <div className="step-text">
                                <strong>Account Settings</strong>
                                <p style={{ margin: 0 }}>Manage API tokens and KYC documents.</p>
                            </div>
                            <button className="btn btn-sm btn-secondary" onClick={() => navigate('/dashboard/settings')} style={{ marginLeft: 'auto' }}>
                                Manage Account
                            </button>
                        </div>

                        <div className="step-item" style={{ alignItems: 'center', marginBottom: 0 }}>
                            <div className="step-text">
                                <strong>Documentation</strong>
                                <p style={{ margin: 0 }}>Learn how to optimize your workflow.</p>
                            </div>
                            <Link to="/docs" className="btn btn-sm btn-secondary" style={{ marginLeft: 'auto' }}>
                                Read Docs
                            </Link>
                        </div>
                    </div>
                </div>

                {/* DNS Setup Guide Card */}
                <div className="card overview-card">
                    <h3>🌐 DNS Setup Guide</h3>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1rem' }}>
                        After adding a site, point your domain to Wranglr with these two steps:
                    </p>

                    <div style={{ background: 'var(--bg-secondary)', padding: '1rem', borderRadius: '8px', border: '1px solid var(--border-color)', marginBottom: '1rem' }}>
                        <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.9rem' }}>Step 1: Add a CNAME Record</div>
                        <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                                    <th style={{ padding: '0.4rem 0', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: 500 }}>Type</th>
                                    <th style={{ padding: '0.4rem 0', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: 500 }}>Name</th>
                                    <th style={{ padding: '0.4rem 0', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: 500 }}>Target</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={{ padding: '0.4rem 0' }}><code style={{ background: 'var(--accent-primary)', color: '#fff', padding: '0.15rem 0.5rem', borderRadius: '4px', fontSize: '0.8rem' }}>CNAME</code></td>
                                    <td style={{ padding: '0.4rem 0' }}><code>www</code></td>
                                    <td style={{ padding: '0.4rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <code>proxy.wranglr.co.za</code>
                                        <button
                                            onClick={() => { navigator.clipboard.writeText('proxy.wranglr.co.za'); alert('Copied!'); }}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '0.1rem' }}
                                            title="Copy"
                                        >📋</button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div style={{ background: 'var(--bg-secondary)', padding: '1rem', borderRadius: '8px', border: '1px solid var(--border-color)', marginBottom: '1rem' }}>
                        <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.9rem' }}>Step 2: Forward Root Domain (Recommended)</div>
                        <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                            In your registrar's settings, set up a <strong>301 redirect</strong> from your naked domain:<br />
                            <code>example.co.za</code> → <code>https://www.example.co.za</code><br />
                            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted, var(--text-secondary))' }}>This ensures visitors without "www" still reach your site.</span>
                        </p>
                    </div>

                    <div style={{ background: 'rgba(99, 102, 241, 0.06)', padding: '0.75rem', borderRadius: '6px', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                        ⏳ DNS changes take <strong>15–60 minutes</strong> to propagate. Use the "Check DNS" button on your site card to verify.
                    </div>
                </div>

                {/* CLI Quick Start Card */}
                <div className="card overview-card">
                    <h3>⚡ CLI Quick Start</h3>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1rem' }}>
                        Deploy from your terminal — no GUI needed.
                    </p>

                    <div style={{ background: '#1a1a2e', padding: '1rem', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                        <code style={{ color: '#9ca3af', fontFamily: 'monospace', fontSize: '0.8rem' }}># Install the CLI</code>
                        <code style={{ color: '#e0e0e0', fontFamily: 'monospace', fontSize: '0.85rem' }}>npm install -g @wranglr/cli</code>
                        <code style={{ color: '#9ca3af', fontFamily: 'monospace', fontSize: '0.8rem', marginTop: '0.5rem' }}># Log in with your API token</code>
                        <code style={{ color: '#e0e0e0', fontFamily: 'monospace', fontSize: '0.85rem' }}>wranglr login --token YOUR_TOKEN</code>
                        <code style={{ color: '#9ca3af', fontFamily: 'monospace', fontSize: '0.8rem', marginTop: '0.5rem' }}># Deploy your site</code>
                        <code style={{ color: '#e0e0e0', fontFamily: 'monospace', fontSize: '0.85rem' }}>wranglr deploy ./dist --site www.example.co.za</code>
                        <code style={{ color: '#10b981', fontFamily: 'monospace', fontSize: '0.85rem', marginTop: '0.5rem' }}>✓ Deployed 42 files to the Edge</code>
                    </div>

                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button className="btn btn-sm btn-secondary" onClick={() => navigate('/dashboard/settings')} style={{ flex: 1 }}>
                            🔑 Get API Token
                        </button>
                        <Link to="/docs" className="btn btn-sm btn-secondary" style={{ flex: 1, textAlign: 'center' }}>
                            📖 Full CLI Docs
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Sites Tab
function SitesTab() {
    const [sites, setSites] = useState<Site[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [newDomain, setNewDomain] = useState('');
    const [addError, setAddError] = useState('');
    const [dnsInstructions, setDnsInstructions] = useState<{ domain: string; target: string } | null>(null);
    const [launching, setLaunching] = useState(false);

    const [error, setError] = useState('');

    useEffect(() => {
        loadSites();
    }, []);

    const loadSites = async () => {
        try {
            const response = await api.getSites();
            if (response.success && response.data) {
                setSites(response.data);
            } else {
                setError(response.error || 'Failed to load sites');
            }
        } catch (err) {
            setError('Network error while loading sites');
        }
        setLoading(false);
    };

    const handleAddSite = async (e: React.FormEvent) => {
        e.preventDefault();
        setAddError('');
        setLaunching(true);

        let domainToAdd = newDomain.toLowerCase().trim();

        // Warning for root domains (no subdomains)
        // e.g., "example.com" vs "www.example.com" or "app.example.com"
        const isRootDomain = (domainToAdd.match(/\./g) || []).length === 1 && !domainToAdd.startsWith('www.');

        if (isRootDomain) {
            const useWWW = confirm(
                `Recommendation: Use www.${domainToAdd} instead.\n\n` +
                `Root domains (like ${domainToAdd}) are harder to configure with many registrars (GoDaddy, Namecheap).\n\n` +
                `Click 'OK' to use www.${domainToAdd} (Recommended)\n` +
                `Click 'Cancel' to keep ${domainToAdd}`
            );

            if (useWWW) {
                domainToAdd = `www.${domainToAdd}`;
            }
        }

        let success = false;
        let launchDnsTarget = '';

        const response = await api.createSite(domainToAdd);
        if (response.success && response.data) {
            success = true;
            launchDnsTarget = response.data.dnsInstructions?.target || 'proxy.wranglr.co.za';
            setSites([response.data, ...sites]);
        } else {
            setAddError(response.error || 'Failed to add site');
            setLaunching(false);
            return;
        }

        if (success) {
            setNewDomain('');
            setShowAddModal(false);
            setLaunching(false);
            setDnsInstructions({
                domain: domainToAdd,
                target: launchDnsTarget
            });
        }
    };

    const handleDeleteSite = async (siteId: string) => {
        if (!confirm('Are you sure you want to delete this site?')) return;

        const response = await api.deleteSite(siteId);
        if (response.success) {
            setSites(sites.filter(s => s.id !== siteId));
        }
    };

    const handlePurgeCache = async (siteId: string) => {
        const response = await api.purgeCache(siteId);
        if (response.success) {
            alert('Cache purged successfully!');
        }
    };

    const [showDnsModal, setShowDnsModal] = useState<string | null>(null);
    const [dnsStatus, setDnsStatus] = useState<Record<string, any>>({});
    const [checkingDns, setCheckingDns] = useState<string | null>(null);

    const handleCheckDns = async (siteId: string) => {
        setCheckingDns(siteId);
        const response = await api.checkDnsStatus(siteId);
        if (response.success && response.data) {
            setDnsStatus(prev => ({ ...prev, [siteId]: response.data }));
        }
        setCheckingDns(null);
    };

    if (loading) return <div className="loading">Loading sites...</div>;

    return (
        <div className="tab-content">
            <div className="tab-header">
                <h2>My Sites</h2>
                <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
                    + Add Site
                </button>
            </div>

            {dnsInstructions && (
                <div className="alert alert-info dns-banner">
                    <div className="dns-banner-content">
                        <span className="dns-icon">🔧</span>
                        <div>
                            <strong>Almost there!</strong> Your site <code>{dnsInstructions.domain}</code> needs DNS configuration.
                        </div>
                    </div>
                    <button className="btn btn-primary" onClick={() => setShowDnsModal(dnsInstructions.domain)}>
                        View Setup Instructions
                    </button>
                </div>
            )}

            {sites.length === 0 ? (
                <div className="empty-state">
                    <p>You haven't added any sites yet.</p>
                    <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
                        Add Your First Site
                    </button>
                </div>
            ) : (
                <div className="sites-grid">
                    {sites.map(site => (
                        <div key={site.id} className="site-card">
                            <div className="site-header">
                                <h3>{site.domain_name}</h3>
                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                    <span className={`badge badge-${site.status === 'active' ? 'success' : 'warning'}`}>
                                        {site.status}
                                    </span>
                                    {dnsStatus[site.id] && (
                                        <span className={`badge badge-${dnsStatus[site.id].status === 'propagated' ? 'success' :
                                            dnsStatus[site.id].status === 'propagating' ? 'warning' : 'error'
                                            }`} title={dnsStatus[site.id].message}>
                                            {dnsStatus[site.id].status === 'propagated' ? '✓ DNS OK' :
                                                dnsStatus[site.id].status === 'propagating' ? '⏳ DNS Propagating' : '⚠ DNS Not Set'}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="site-dns-info" style={{ margin: '0.75rem 0', padding: '0.75rem', background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                                    <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)' }}>🌐 DNS Configuration</span>
                                    <button className="btn btn-sm btn-secondary" onClick={() => setShowDnsModal(site.domain_name)} style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}>
                                        Full Guide
                                    </button>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem' }}>
                                    <span style={{ color: 'var(--text-secondary)' }}>CNAME →</span>
                                    <code style={{ background: 'var(--bg-tertiary, var(--bg-primary))', padding: '0.2rem 0.5rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.8rem', flex: 1 }}>proxy.wranglr.co.za</code>
                                    <button
                                        className="btn-icon"
                                        onClick={() => { navigator.clipboard.writeText('proxy.wranglr.co.za'); alert('Copied!'); }}
                                        title="Copy CNAME target"
                                        style={{ padding: '0.2rem', cursor: 'pointer', background: 'none', border: 'none', color: 'var(--text-secondary)' }}
                                    >
                                        📋
                                    </button>
                                </div>
                                <div style={{ marginTop: '0.5rem', padding: '0.4rem 0.6rem', background: 'rgba(99, 102, 241, 0.06)', borderRadius: '4px', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                    💡 <strong>Tip:</strong> Also set up a <strong>domain forward</strong> from <code>{site.domain_name.replace('www.', '')}</code> → <code>https://www.{site.domain_name.replace('www.', '')}</code> so users without "www" still reach your site.
                                </div>
                                {site.status === 'pending' && (
                                    <div style={{ marginTop: '0.5rem', padding: '0.4rem 0.6rem', background: 'rgba(255, 193, 7, 0.1)', borderRadius: '4px', fontSize: '0.75rem', color: 'var(--color-warning, #f59e0b)' }}>
                                        ⚠️ DNS not configured yet — point your domain to <code>proxy.wranglr.co.za</code> to go live.
                                    </div>
                                )}
                            </div>
                            <div className="site-storage" style={{ margin: '0.75rem 0', padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '6px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                                    <span>Storage Used</span>
                                    <span>{formatBytes(site.storage_used)}</span>
                                </div>
                                <div style={{
                                    height: '6px',
                                    background: 'var(--bg-tertiary)',
                                    borderRadius: '3px',
                                    overflow: 'hidden'
                                }}>
                                    <div style={{
                                        width: `${Math.min((site.storage_used / (500 * 1024 * 1024)) * 100, 100)}%`,
                                        height: '100%',
                                        background: site.storage_used > (500 * 1024 * 1024 * 0.9) ? 'var(--color-error)' :
                                            site.storage_used > (500 * 1024 * 1024 * 0.7) ? 'var(--color-warning)' : 'var(--color-success)',
                                        borderRadius: '3px',
                                        transition: 'width 0.3s ease'
                                    }} />
                                </div>
                            </div>
                            <div className="site-actions">
                                <a
                                    href={`https://${site.domain_name}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-primary"
                                >
                                    🌐 View Site
                                </a>
                                <Link to={`/dashboard/editor/${site.id}`} className="btn btn-primary" style={{ background: '#8b5cf6', borderColor: '#8b5cf6' }}>
                                    ✏️ Edit Code
                                </Link>
                                <a
                                    href={`https://${site.id}-preview.wranglr.co.za`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-secondary"
                                    title="Preview before DNS is configured"
                                >
                                    🧪 Dev Preview
                                </a>
                                <Link to={`/dashboard/files/${site.id}`} className="btn btn-secondary">
                                    Manage Files
                                </Link>
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => handleCheckDns(site.id)}
                                    disabled={checkingDns === site.id}
                                >
                                    {checkingDns === site.id ? '⏳ Checking...' : '🔍 Check DNS'}
                                </button>
                                <button className="btn btn-secondary" onClick={() => handlePurgeCache(site.id)}>
                                    Clear Cache
                                </button>
                                <button className="btn btn-danger" onClick={() => handleDeleteSite(site.id)}>
                                    Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* DNS Setup Modal */}
            {error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>}

            {showDnsModal && (
                <div className="modal-overlay" onClick={() => setShowDnsModal(null)} style={{ alignItems: 'flex-start', overflowY: 'auto', paddingTop: '3rem' }}>
                    <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ height: 'auto', maxHeight: 'none', overflow: 'visible', margin: 'auto', maxWidth: '900px', width: '95%' }}>
                        <div className="modal-header">
                            <h3 className="modal-title">🌐 Connect Your Domain (Updated)</h3>
                            <button className="modal-close" onClick={() => setShowDnsModal(null)}>×</button>
                        </div>
                        <div className="modal-body dns-guide" style={{ overflow: 'visible', maxHeight: 'none', height: 'auto', padding: '2rem' }}>

                            {/* Provider Selector */}
                            <div className="provider-selector">
                                <p style={{ marginBottom: '0.5rem', fontWeight: 600 }}>Where did you buy your domain?</p>
                                <div className="btn-group">
                                    <button className="btn btn-sm btn-secondary">GoDaddy / Namecheap</button>
                                    <button className="btn btn-sm btn-secondary">Cloudflare</button>
                                    <button className="btn btn-sm btn-secondary">Other</button>
                                </div>
                            </div>

                            <div className="dns-step">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <h4>The Golden Rule: Use "www"</h4>
                                    <p>
                                        <strong>Most registrars (like GoDaddy) DO NOT support</strong> CNAME records on the root domain (e.g., <code>{showDnsModal.replace('www.', '')}</code>).
                                    </p>
                                    <p>
                                        We strongly recommend using <code>www.{showDnsModal.replace('www.', '')}</code> for your site.
                                    </p>
                                </div>
                            </div>

                            <div className="dns-step">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <h4>Add the DNS Record</h4>
                                    <p>Go to your registrar's <strong>DNS Management</strong> page and add this record:</p>
                                    <div className="dns-record-box">
                                        <table className="dns-table">
                                            <thead>
                                                <tr>
                                                    <th>Type</th>
                                                    <th>Name (Host)</th>
                                                    <th>Value (Target)</th>
                                                    <th>TTL</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td><code className="badge-primary">CNAME</code></td>
                                                    <td><code>www</code></td>
                                                    <td><code>proxy.wranglr.co.za</code></td>
                                                    <td>1 Hour / Auto</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            <div className="dns-step">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <h4>Redirect the Root Domain (Crucial!)</h4>
                                    <p>To ensure people can type just <code>{showDnsModal.replace('www.', '')}</code> and reach your site:</p>
                                    <ul className="step-list">
                                        <li>Look for "Domain Forwarding" or "Redirects" in your registrar settings.</li>
                                        <li><strong>Forward:</strong> <code>{showDnsModal.replace('www.', '')}</code></li>
                                        <li><strong>To:</strong> <code>https://www.{showDnsModal.replace('www.', '')}</code></li>
                                        <li><strong>Type:</strong> 301 Permanent (Forward Only)</li>
                                    </ul>
                                </div>
                            </div>

                            <div className="dns-step warning-step">
                                <div className="step-number">⏳</div>
                                <div className="step-content">
                                    <h4>Wait for the Internet (Propagation)</h4>
                                    <p>DNS changes aren't instant! It usually takes <strong>15 to 60 minutes</strong>.</p>
                                    <p>If you see an error (SSL Mismatch, 522, 523), <strong>wait 30 mins and try again.</strong></p>
                                </div>
                            </div>

                        </div>
                        <div className="modal-footer">
                            <a
                                href={`https://dnschecker.org/#CNAME/www.${showDnsModal.replace('www.', '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-secondary"
                            >
                                🔎 Check DNS Status
                            </a>
                            <button className="btn btn-primary" onClick={() => { setShowDnsModal(null); setDnsInstructions(null); }}>
                                I've Added It
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showAddModal && (
                <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">Add New Site</h3>
                            <button className="modal-close" onClick={() => setShowAddModal(false)}>×</button>
                        </div>
                        <form onSubmit={handleAddSite}>
                            {addError && <div className="alert alert-error">{addError}</div>}
                            <div className="form-group">
                                <label className="form-label">Domain Name</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder="mysite.co.za"
                                    value={newDomain}
                                    onChange={e => setNewDomain(e.target.value)}
                                    required
                                />
                                <small className="form-hint">Enter your domain without http:// or www.</small>
                            </div>

                            <button type="submit" className="btn btn-primary btn-full" disabled={launching}>
                                {launching ? '⏳ Launching...' : 'Add Web Property'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

// Files Tab
function FilesTab() {
    const { siteId } = useLocation().pathname.match(/\/files\/(?<siteId>[^/]+)/)?.groups || {};
    const [files, setFiles] = useState<{ name: string; size: number; lastModified: string }[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);

    // Upload progress state
    const [uploadProgress, setUploadProgress] = useState<{
        current: number;
        total: number;
        currentFile: string;
        percentage: number;
    } | null>(null);

    // Editor State
    const editorRef = useRef<any>(null);
    const [editingFile, setEditingFile] = useState<string | null>(null);
    const [editorContent, setEditorContent] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (siteId) loadFiles();
    }, [siteId]);

    const loadFiles = async () => {
        if (!siteId) return;
        const response = await api.getFiles(siteId);
        if (response.success && response.data) {
            setFiles(response.data);
        }
        setLoading(false);
    };

    const handleEditFile = async (filename: string) => {
        if (!siteId) return;
        try {
            // Check if file is editable (text based)
            const isText = /\.(html|css|js|json|txt|md|xml|svg)$/i.test(filename);
            if (!isText && !confirm('This file might be binary. Edit as text anyway?')) return;

            const content = await api.getFileContent(siteId, filename);
            setEditorContent(content);
            setEditingFile(filename);
        } catch (err) {
            alert('Failed to load file content');
            console.error(err);
        }
    };

    const handleSaveEditor = async () => {
        if (!siteId || !editingFile) return;
        setIsSaving(true);
        try {
            // Create a File object from the content
            const blob = new Blob([editorContent], { type: 'text/plain' });
            const file = new File([blob], editingFile, { type: 'text/plain' });

            // Reuse upload logic (it handles the API call)
            await uploadFiles([{ file, path: editingFile }]);

            setEditingFile(null); // Close editor
        } catch (err) {
            alert('Failed to save file');
        } finally {
            setIsSaving(false);
        }
    };

    const handleEditorDidMount = (editor: any, monaco: any) => {
        editorRef.current = editor;
    };

    const handleFormatCode = () => {
        if (editorRef.current) {
            editorRef.current.getAction('editor.action.formatDocument').run();
        }
    };

    const handleDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
        if (!siteId) return;

        // capture items immediately relative to the event
        const items = Array.from(e.dataTransfer.items || []);
        const fileList: { file: File; path: string }[] = [];

        const scanEntry = async (entry: FileSystemEntry, basePath: string = ''): Promise<void> => {
            if (entry.isFile) {
                const fileEntry = entry as FileSystemFileEntry;
                try {
                    const file = await new Promise<File>((resolve, reject) => fileEntry.file(resolve, reject));
                    const path = basePath ? `${basePath}/${entry.name}` : entry.name;
                    fileList.push({ file, path });
                } catch (err) {
                    console.error(`Error reading file ${entry.name}:`, err);
                }
            } else if (entry.isDirectory) {
                const dirEntry = entry as FileSystemDirectoryEntry;
                const dirReader = dirEntry.createReader();

                // Helper to read all entries (handling batches)
                const readEntries = async (): Promise<FileSystemEntry[]> => {
                    return new Promise((resolve, reject) => dirReader.readEntries(resolve, reject));
                };

                try {
                    // recursively read directory
                    // Note: In production you might need to loop readEntries until empty for huge dirs
                    const entries = await readEntries();
                    const newBase = basePath ? `${basePath}/${entry.name}` : entry.name;
                    for (const child of entries) {
                        await scanEntry(child, newBase);
                    }
                } catch (err) {
                    console.error(`Error reading directory ${entry.name}:`, err);
                }
            }
        };



        // Process dropped items
        const entries = items.map(item => item.webkitGetAsEntry ? item.webkitGetAsEntry() : null).filter(Boolean);

        if (entries.length > 0) {
            await Promise.all(entries.map(entry => scanEntry(entry!)));

            if (fileList.length > 0) {
                await uploadFiles(fileList);
            }
        } else {
            // Fallback for browsers that don't support webkitGetAsEntry
            const files = Array.from(e.dataTransfer.files);
            if (files.length > 0) {
                await uploadFiles(files.map(f => ({ file: f, path: f.name })));
            }
        }
    };

    // Handle Zip Upload
    const handleZipUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            setUploading(true);
            const zip = new JSZip();
            const contents = await zip.loadAsync(file);

            const fileList: { file: File; path: string }[] = [];

            // Iterate through zip contents
            const promises: Promise<void>[] = [];

            contents.forEach((relativePath, zipEntry) => {
                if (!zipEntry.dir) {
                    const promise = zipEntry.async('blob').then(blob => {
                        // Create File object
                        const extractedFile = new File([blob], zipEntry.name.split('/').pop() || 'unknown', {
                            type: blob.type || 'application/octet-stream' // fallback
                        });
                        fileList.push({ file: extractedFile, path: relativePath });
                    });
                    promises.push(promise);
                }
            });

            await Promise.all(promises);

            if (fileList.length > 0) {
                await uploadFiles(fileList);
            } else {
                alert('Zip file is empty or contains only folders.');
            }

        } catch (err) {
            console.error("Zip Error:", err);
            alert('Failed to process zip file.');
        } finally {
            setUploading(false);
            // Reset input
            e.target.value = '';
        }
    };

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!siteId || !e.target.files) return;
        const selectedFiles = Array.from(e.target.files);
        // Use webkitRelativePath for folder uploads, fallback to name for single files
        const filesWithPaths = selectedFiles.map(f => ({
            file: f,
            path: (f as any).webkitRelativePath || f.name
        }));
        await uploadFiles(filesWithPaths);
        e.target.value = ''; // Reset input
    };

    const uploadFiles = async (filesToUpload: { file: File; path: string }[]) => {
        if (!siteId) return;
        setUploading(true);
        setUploadProgress({ current: 0, total: filesToUpload.length, currentFile: '', percentage: 0 });

        const token = localStorage.getItem('token');

        for (let i = 0; i < filesToUpload.length; i++) {
            const { file, path } = filesToUpload[i];
            setUploadProgress({
                current: i + 1,
                total: filesToUpload.length,
                currentFile: path,
                percentage: Math.round((i / filesToUpload.length) * 100)
            });

            try {
                // Use XMLHttpRequest for progress tracking
                await new Promise<void>((resolve, reject) => {
                    const xhr = new XMLHttpRequest();
                    xhr.open('POST', `https://api.wranglr.co.za/api/upload/direct/${siteId}/${encodeURIComponent(path)}`);
                    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
                    xhr.setRequestHeader('Authorization', `Bearer ${token}`);

                    xhr.upload.onprogress = (e) => {
                        if (e.lengthComputable) {
                            const fileProgress = Math.round((e.loaded / e.total) * 100);
                            const overallProgress = Math.round(((i + (e.loaded / e.total)) / filesToUpload.length) * 100);
                            setUploadProgress(prev => prev ? {
                                ...prev,
                                percentage: overallProgress
                            } : null);
                        }
                    };

                    xhr.onload = () => {
                        if (xhr.status >= 200 && xhr.status < 300) {
                            resolve();
                        } else {
                            console.error('Upload failed:', xhr.responseText);
                            resolve(); // Continue with other files
                        }
                    };

                    xhr.onerror = () => {
                        console.error('Upload error for', path);
                        resolve(); // Continue with other files
                    };

                    xhr.send(file);
                });
            } catch (error) {
                console.error('Upload error:', error);
            }
        }

        setUploadProgress({ current: filesToUpload.length, total: filesToUpload.length, currentFile: 'Done!', percentage: 100 });
        await loadFiles();

        // Clear progress after a brief delay
        setTimeout(() => {
            setUploadProgress(null);
            setUploading(false);
        }, 1000);
    };

    const handleDeleteFile = async (filename: string) => {
        if (!siteId || !confirm(`Delete ${filename}?`)) return;

        const response = await api.deleteFile(siteId, filename);
        if (response.success) {
            setFiles(files.filter(f => f.name !== filename));
        }
    };

    const handleDeleteAllFiles = async () => {
        if (!siteId || files.length === 0) return;
        if (!confirm('Are you definitely sure? This will delete ALL files for this site. This cannot be undone.')) return;

        const response = await api.deleteAllFiles(siteId);
        if (response.success) {
            setFiles([]);
            alert('All files deleted successfully.');
        } else {
            alert('Failed to delete files: ' + (response.error || 'Unknown error'));
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    if (!siteId) {
        return <div className="empty-state">Select a site to manage files</div>;
    }

    if (loading) return <div className="loading">Loading files...</div>;

    return (
        <div className="tab-content">
            <div className="tab-header">
                <h2>File Manager</h2>
                <div className="file-actions">
                    <button className="btn btn-danger" onClick={handleDeleteAllFiles} disabled={files.length === 0}>
                        Delete All Files
                    </button>
                    <Link to="/dashboard/sites" className="btn btn-secondary">← Back to Sites</Link>
                </div>
            </div>

            <div
                className={`drop-zone ${dragOver ? 'drag-over' : ''}`}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
            >
                <div className="drop-zone-content">
                    <span className="drop-icon">📁</span>
                    <p>Drag and drop files or folders here, or</p>
                    <div className="upload-buttons">
                        <label className="btn btn-primary" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            📦 Upload Zip
                            <input type="file" accept=".zip" onChange={handleZipUpload} hidden disabled={uploading} />
                        </label>
                    </div>
                </div>
            </div>

            {/* Upload Progress */}
            {uploadProgress && (
                <div className="upload-progress">
                    <div className="progress-header">
                        <span>Uploading: {uploadProgress.current} / {uploadProgress.total} files</span>
                        <span>{uploadProgress.percentage}%</span>
                    </div>
                    <div className="progress-bar-container">
                        <div className="progress-bar" style={{ width: `${uploadProgress.percentage}%` }}></div>
                    </div>
                    <div className="progress-file">{uploadProgress.currentFile}</div>
                </div>
            )}

            <div className="card overview-card full-width">
                <div className="card-header">
                    <h2>
                        File Manager
                        {files && files.length > 0 && <span className="badge">{files.length} Files</span>}
                    </h2>
                </div>
                {loading ? (
                    <div className="loading-state">Loading files...</div>
                ) : (
                    <div className="file-manager-container">
                        {/* File Tree View */}
                        <FileTree
                            files={files || []}
                            onEdit={(path) => {
                                setEditingFile(path);
                                // Identify content language and load it?
                                // Actually editorContent handles loading. 
                                // Wait, setEditingFile just opens the modal. 
                                // The modal content loading happens in useEffect when editingFile changes.
                                // AND we need to load content! 
                                // My existing logic uses: const [editingFile, setEditingFile]...
                                // And: useEffect(() => { if (editingFile && siteId) loadFileContent(); }, [editingFile]);
                                // So passing path is enough.
                            }}
                            onDelete={handleDeleteFile}
                        />
                    </div>
                )
                }
            </div >

            {/* Editor Modal */}
            {
                editingFile && (
                    <div className="modal-overlay">
                        <div className="editor-modal">
                            <div className="editor-header">
                                <span className="editor-title">{editingFile}</span>
                                <div className="editor-actions">
                                    <button className="btn btn-secondary btn-sm" onClick={() => setEditingFile(null)}>Close</button>
                                    <button className="btn btn-secondary btn-sm" onClick={handleFormatCode} disabled={isSaving} title="Format Code">Prettify</button>
                                    <button className="btn btn-primary btn-sm" onClick={handleSaveEditor} disabled={isSaving}>
                                        {isSaving ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>
                            </div>
                            <div className="editor-content">
                                {isImageFile(editingFile) ? (
                                    <div className="image-preview">
                                        <img
                                            src={`data:image/${editingFile.split('.').pop()};base64,${btoa(editorContent)}`}
                                            alt={editingFile}
                                            style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                                        />
                                        <p className="image-note">Image files cannot be edited. Use the upload feature to replace.</p>
                                    </div>
                                ) : (
                                    <Editor
                                        height="100%"
                                        language={getLanguageFromFilename(editingFile)}
                                        value={editorContent}
                                        onChange={(value) => setEditorContent(value || '')}
                                        onMount={handleEditorDidMount}
                                        theme="vs-dark"
                                        options={{
                                            minimap: { enabled: false },
                                            fontSize: 13,
                                            lineNumbers: 'on',
                                            wordWrap: 'on',
                                            wrappingStrategy: 'advanced',
                                            automaticLayout: true,
                                            scrollBeyondLastLine: false,
                                            padding: { top: 10 },
                                            maxTokenizationLineLength: 30000, // Fix for minified files warning
                                            scrollbar: {
                                                vertical: 'auto',
                                                horizontal: 'auto',
                                            },
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
}


// A/B Testing Tab
function ABTestingTab() {
    const [sites, setSites] = useState<Site[]>([]);
    const [selectedSiteId, setSelectedSiteId] = useState<string>('');
    const [tests, setTests] = useState<api.ABTest[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);

    // Form state
    const [newTest, setNewTest] = useState({ path: '/', variant_a_path: '/index.html', variant_b_path: '/index-b.html', traffic_split: 0.5 });

    useEffect(() => {
        loadSites();
    }, []);

    useEffect(() => {
        if (selectedSiteId) {
            loadTests(selectedSiteId);
        } else {
            setTests([]);
        }
    }, [selectedSiteId]);

    const loadSites = async () => {
        const res = await api.getSites();
        if (res.success && res.data) {
            setSites(res.data);
            if (res.data.length > 0) setSelectedSiteId(res.data[0].id);
        }
        setLoading(false);
    };

    const loadTests = async (siteId: string) => {
        setLoading(true);
        const res = await api.getABTests(siteId);
        if (res.success && res.data) setTests(res.data);
        setLoading(false);
    };

    const handleCreateTest = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedSiteId) return;
        const res = await api.createABTest(selectedSiteId, newTest);
        if (res.success) {
            setShowCreateModal(false);
            loadTests(selectedSiteId);
        } else {
            alert(res.error || 'Failed to create test');
        }
    };

    const handleStatusChange = async (testId: string, status: 'active' | 'paused' | 'stopped') => {
        if (!selectedSiteId) return;
        const res = await api.updateABTestStatus(selectedSiteId, testId, status);
        if (res.success) loadTests(selectedSiteId);
    };

    return (
        <div className="tab-content">
            <div className="tab-header">
                <h2>A/B Testing</h2>
                <div style={{ display: 'flex', gap: '1rem' }}>
                    <select
                        value={selectedSiteId}
                        onChange={e => setSelectedSiteId(e.target.value)}
                        className="form-input"
                        style={{ width: 'auto' }}
                    >
                        {sites.map(s => <option key={s.id} value={s.id}>{s.domain_name}</option>)}
                    </select>
                    <button className="btn btn-primary" onClick={() => setShowCreateModal(true)} disabled={!selectedSiteId}>
                        + New Test
                    </button>
                </div>
            </div>

            {loading ? <div className="loading">Loading...</div> : (
                <div className="tests-list">
                    {tests.length === 0 ? (
                        <div className="empty-state">
                            <p>No A/B tests active for this site.</p>
                        </div>
                    ) : (
                        tests.map(test => (
                            <div key={test.id} className="card ab-test-card" style={{ marginBottom: '1rem' }}>
                                <div className="test-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                                    <h3>{test.path}</h3>
                                    <div className="test-status">
                                        <span className={`badge badge-${test.status}`}>{test.status}</span>
                                        <select
                                            value={test.status}
                                            onChange={(e) => handleStatusChange(test.id, e.target.value as any)}
                                            style={{ marginLeft: '1rem', padding: '0.2rem' }}
                                        >
                                            <option value="active">Active</option>
                                            <option value="paused">Paused</option>
                                            <option value="stopped">Stopped</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="test-details" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                    <div className="variant-box p-3 bg-dark rounded">
                                        <strong>Variant A (Control)</strong>
                                        <div>Path: <code>{test.variant_a_path}</code></div>
                                        <div>Views: {test.stats?.views_a || 0}</div>
                                    </div>
                                    <div className="variant-box p-3 bg-dark rounded">
                                        <strong>Variant B (Test)</strong>
                                        <div>Path: <code>{test.variant_b_path}</code></div>
                                        <div>Views: {test.stats?.views_b || 0}</div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {showCreateModal && (
                <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <h3>Create A/B Test</h3>
                        <form onSubmit={handleCreateTest}>
                            <div className="form-group">
                                <label>Route Path (e.g. /)</label>
                                <input className="form-input" value={newTest.path} onChange={e => setNewTest({ ...newTest, path: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Variant A File (e.g. /index.html)</label>
                                <input className="form-input" value={newTest.variant_a_path} onChange={e => setNewTest({ ...newTest, variant_a_path: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Variant B File (e.g. /index-b.html)</label>
                                <input className="form-input" value={newTest.variant_b_path} onChange={e => setNewTest({ ...newTest, variant_b_path: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Traffic Split (0.5 = 50/50)</label>
                                <input type="number" step="0.1" max="1" min="0" className="form-input" value={newTest.traffic_split} onChange={e => setNewTest({ ...newTest, traffic_split: parseFloat(e.target.value) })} />
                            </div>
                            <button type="submit" className="btn btn-primary btn-full">Start Test</button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

// Contact Support Tab
function ContactTab() {
    const [threads, setThreads] = useState<api.ContactThread[]>([]);
    const [loading, setLoading] = useState(true);
    const [showNewTicket, setShowNewTicket] = useState(false);
    const [newTicket, setNewTicket] = useState({ subject: '', message: '' });
    const [selectedThread, setSelectedThread] = useState<api.ContactThread | null>(null);
    const [replyContent, setReplyContent] = useState('');

    useEffect(() => {
        loadThreads();
    }, []);

    const loadThreads = async () => {
        setLoading(true);
        const res = await api.getContactThreads();
        if (res.success && res.data) setThreads(res.data);
        setLoading(false);
    };

    const handleCreateTicket = async (e: React.FormEvent) => {
        e.preventDefault();
        const res = await api.createContactThread(newTicket.subject, newTicket.message);
        if (res.success) {
            setShowNewTicket(false);
            setNewTicket({ subject: '', message: '' });
            loadThreads();
        } else {
            alert(res.error || 'Failed to create ticket');
        }
    };

    const handleReply = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedThread) return;
        const res = await api.replyToThread(selectedThread.id, replyContent);
        if (res.success) {
            setReplyContent('');
            // Refresh thread to see new message
            // Since api.getContactThreads returns list, we just reload list and find it
            // Ideal would be returning the updated thread
            const updatedList = await api.getContactThreads();
            if (updatedList.success && updatedList.data) {
                setThreads(updatedList.data);
                const updatedThread = updatedList.data.find(t => t.id === selectedThread.id);
                if (updatedThread) setSelectedThread(updatedThread);
            }
        }
    };

    return (
        <div className="tab-content">
            <div className="tab-header">
                <h2>Support & Contact</h2>
                <button className="btn btn-primary" onClick={() => setShowNewTicket(true)}>+ New Ticket</button>
            </div>

            <div className="support-layout" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '2rem', height: '600px' }}>
                <div className="threads-list" style={{ overflowY: 'auto', borderRight: '1px solid var(--border-color)', paddingRight: '1rem' }}>
                    {loading ? <div>Loading...</div> : threads.map(thread => (
                        <div
                            key={thread.id}
                            className={`card thread-item ${selectedThread?.id === thread.id ? 'active' : ''}`}
                            onClick={() => setSelectedThread(thread)}
                            style={{ cursor: 'pointer', marginBottom: '1rem', border: selectedThread?.id === thread.id ? '1px solid var(--accent-primary)' : '' }}
                        >
                            <h4>{thread.subject}</h4>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                                <span className={`badge badge-${thread.status}`}>{thread.status}</span>
                                <span style={{ color: 'var(--text-muted)' }}>{new Date(thread.created_at).toLocaleDateString()}</span>
                            </div>
                        </div>
                    ))}
                    {!loading && threads.length === 0 && <p>No support tickets yet.</p>}
                </div>

                <div className="thread-view">
                    {selectedThread ? (
                        <div className="thread-detail" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <div className="thread-header" style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid var(--border-color)' }}>
                                <h3>{selectedThread.subject}</h3>
                            </div>
                            <div className="messages-area" style={{ height: '350px', overflowY: 'auto', marginBottom: '1rem' }}>
                                {selectedThread.messages?.map(msg => (
                                    <div key={msg.id} className={`message ${msg.sender_type}`} style={{
                                        marginBottom: '1rem',
                                        padding: '1rem',
                                        borderRadius: '8px',
                                        background: msg.sender_type === 'user' ? 'var(--bg-secondary)' : 'rgba(var(--accent-primary-rgb), 0.1)',
                                        marginLeft: msg.sender_type === 'user' ? '0' : '2rem',
                                        marginRight: msg.sender_type === 'user' ? '2rem' : '0',
                                        border: msg.sender_type === 'admin' ? '1px solid var(--accent-primary)' : 'none'
                                    }}>
                                        <div className="msg-meta" style={{ fontSize: '0.8rem', marginBottom: '0.5rem', opacity: 0.7 }}>
                                            <strong>{msg.sender_type === 'user' ? 'You' : 'Support Team'}</strong> • {new Date(msg.created_at).toLocaleString()}
                                        </div>
                                        <div className="msg-content" style={{ whiteSpace: 'pre-wrap' }}>{msg.message}</div>
                                    </div>
                                ))}
                            </div>
                            <form onSubmit={handleReply} className="reply-form">
                                <textarea
                                    className="form-input"
                                    value={replyContent}
                                    onChange={e => setReplyContent(e.target.value)}
                                    placeholder="Type your reply..."
                                    rows={4}
                                    required
                                    style={{ marginBottom: '1rem', resize: 'vertical' }}
                                />
                                <button type="submit" className="btn btn-primary" disabled={!replyContent.trim()}>Send Reply</button>
                            </form>
                        </div>
                    ) : (
                        <div className="empty-state">Select a ticket to view conversation</div>
                    )}
                </div>
            </div>

            {showNewTicket && (
                <div className="modal-overlay" onClick={() => setShowNewTicket(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <h3>Open New Support Ticket</h3>
                        <form onSubmit={handleCreateTicket}>
                            <div className="form-group">
                                <label>Subject</label>
                                <input className="form-input" value={newTicket.subject} onChange={e => setNewTicket({ ...newTicket, subject: e.target.value })} required />
                            </div>
                            <div className="form-group">
                                <label>Message</label>
                                <textarea className="form-input" rows={5} value={newTicket.message} onChange={e => setNewTicket({ ...newTicket, message: e.target.value })} required />
                            </div>
                            <button type="submit" className="btn btn-primary btn-full">Submit Ticket</button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

// Billing Tab
function BillingTab() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const handleUpgrade = async (plan: string) => {
        setLoading(true);
        const response = await api.createCheckout(plan, 'hosting');
        if (response.success && response.data) {
            window.location.href = response.data.redirectUrl;
        }
        setLoading(false);
    };

    const isActive = user && isSubscriptionActive(user.subscription_expiry);
    const daysLeft = user ? getDaysRemaining(user.subscription_expiry) : 0;

    return (
        <div className="tab-content">
            <h2>Billing</h2>

            <div className="current-plan card">
                <h3>Current Plan: <span className="gradient-text">{user?.plan_tier && (HOSTING_PLANS[user.plan_tier as keyof typeof HOSTING_PLANS]?.name || user.plan_tier.charAt(0).toUpperCase() + user.plan_tier.slice(1))}</span></h3>
                {isActive ? (
                    <p className="plan-status active">Active - {daysLeft} days remaining</p>
                ) : (
                    <p className="plan-status expired">Expired or inactive</p>
                )}
            </div>

            <h3 className="plans-title" style={{ marginTop: '2rem' }}>Hosting Plans</h3>
            <div className="plans-grid">
                {Object.entries(HOSTING_PLANS).map(([key, plan]) => (
                    <div key={key} className={`plan-card ${key === user?.plan_tier ? 'current' : ''}`}>
                        <h4>{plan.name}</h4>
                        <div className="plan-price">{formatZAR(plan.price)}/month</div>
                        <ul>
                            {plan.features.map((f, i) => <li key={i}>✓ {f}</li>)}
                        </ul>
                        <button
                            className="btn btn-primary btn-full"
                            disabled={loading || (key === user?.plan_tier && Boolean(isActive))}
                            onClick={() => handleUpgrade(key)}
                        >
                            {key === user?.plan_tier
                                ? (isActive ? 'Current Plan' : 'Renew Plan')
                                : 'Upgrade'}
                        </button>
                    </div>
                ))}
            </div>


        </div>
    );
}

// Main Dashboard
export default function DashboardPage() {
    const { user, logout } = useAuth();
    const { theme } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const isActive = (path: string) => location.pathname.includes(path);

    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    return (
        <div className="dashboard">
            {/* Mobile Header (Hamburger) */}
            <header className="mobile-header">
                <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(true)}>
                    ☰
                </button>
                <img
                    src={theme === 'business' ? '/lightlogo.png' : '/darklogo.png'}
                    alt="Wranglr"
                    className="mobile-logo"
                />
            </header>

            {/* Sidebar Overlay (Backdrop) */}
            {mobileMenuOpen && (
                <div className="sidebar-backdrop" onClick={() => setMobileMenuOpen(false)}></div>
            )}

            {/* Sidebar */}
            <aside className={`sidebar ${mobileMenuOpen ? 'open' : ''}`}>
                <div className="sidebar-header">
                    <Link to="/" className="logo">
                        <img
                            src={theme === 'business' ? '/lightlogo.png' : '/darklogo.png'}
                            alt="Wranglr"
                            className="sidebar-logo"
                        />
                    </Link>
                    {/* Close Button (Mobile Only) */}
                    <button className="close-sidebar" onClick={() => setMobileMenuOpen(false)}>
                        ✕
                    </button>
                </div>
                <nav className="sidebar-nav">
                    <Link to="/dashboard" className={`nav-item ${location.pathname === '/dashboard' ? 'active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                        📊 Overview
                    </Link>
                    <Link to="/dashboard/sites" className={`nav-item ${isActive('sites') ? 'active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                        🌍 My Sites
                    </Link>

                    <Link to="/dashboard/billing" className={`nav-item ${isActive('billing') ? 'active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                        💳 Billing
                    </Link>
                    <Link to="/dashboard/security" className={`nav-item ${isActive('security') ? 'active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                        🛡️ Security
                    </Link>
                    <Link to="/dashboard/ab-testing" className={`nav-item ${isActive('ab-testing') ? 'active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                        🅰️ A/B Testing
                    </Link>
                    <Link to="/dashboard/support" className={`nav-item ${isActive('support') ? 'active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                        💬 Support
                    </Link>


                    <Link to="/dashboard/settings" className={`nav-item ${isActive('settings') ? 'active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                        ⚙️ Account Settings
                    </Link>

                    <div className="nav-divider"></div>

                    <a href="/" className="nav-item" onClick={() => setMobileMenuOpen(false)}>
                        🔙 Back to Website
                    </a>
                </nav>
                <div className="sidebar-footer">
                    <div className="user-info">
                        <span className="user-email">{user?.email}</span>
                        <span className="badge badge-success">{user?.plan_tier?.toUpperCase()}</span>
                    </div>

                    <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}>
                        <ThemeSwitcher />
                    </div>

                    <button onClick={handleLogout} className="btn btn-secondary btn-sm btn-full">
                        Sign Out
                    </button>
                    <button onClick={() => { if (confirm('Clear all cached data and reload?')) (window as any).__clearWranglrCache?.(); }} className="btn btn-sm btn-full" style={{ marginTop: '0.5rem', background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                        🔄 Clear Cache & Update
                    </button>
                </div>
            </aside>

            <main className="main-content">
                <Routes>
                    <Route path="/" element={<OverviewTab />} />
                    <Route path="/overview" element={<OverviewTab />} />
                    <Route path="/sites" element={<SitesTab />} />
                    <Route path="/files/:siteId" element={<FilesTab />} />
                    <Route path="/billing" element={<BillingTab />} />
                    <Route path="/ab-testing" element={<ABTestingTab />} />
                    <Route path="/support" element={<ContactTab />} />
                    <Route path="/security" element={<SecurityPage />} />

                </Routes>
            </main>
        </div>
    );
}
